import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Zap, Clock, DollarSign, Shield, CheckCircle, 
  ArrowRight, Play, TrendingUp, FileText, CreditCard,
  Calendar, Menu, X, Rocket, Target, Award, Users,
  Star, MessageSquare, BarChart3, Filter, Share2,
  Globe, Search, Phone, Smartphone, MapPin
} from 'lucide-react';
import { Button } from '../components/ui/button';

const BRAND_PRESENCE_BOOKING_URL = "https://api.forcesystems.io/widget/booking/MY9TsUsbO2gkyDu286JV";

const ContractorLandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showBrandPresenceBooking, setShowBrandPresenceBooking] = useState(false);

  const handleBookDemo = () => {
    window.open('https://calendly.com/guytdunn/homefixer360-demo', '_blank');
  };

  const handleBrandPresenceBooking = () => {
    setShowBrandPresenceBooking(true);
  };

  // Load the booking script when modal opens
  useEffect(() => {
    if (showBrandPresenceBooking) {
      const script = document.createElement('script');
      script.src = 'https://api.forcesystems.io/js/form_embed.js';
      script.type = 'text/javascript';
      document.body.appendChild(script);
      
      return () => {
        if (document.body.contains(script)) {
          document.body.removeChild(script);
        }
      };
    }
  }, [showBrandPresenceBooking]);

  const solutionFeatures = [
    { icon: <Zap className="w-6 h-6" />, text: "Instant satellite-based roof estimates" },
    { icon: <Target className="w-6 h-6" />, text: "Branded homeowner landing pages" },
    { icon: <Clock className="w-6 h-6" />, text: "Automated 5-day follow-up sequence" },
    { icon: <FileText className="w-6 h-6" />, text: "Digital contracts with e-signatures" },
    { icon: <CreditCard className="w-6 h-6" />, text: "Payment processing" },
    { icon: <Shield className="w-6 h-6" />, text: "Insurance claim documentation & tracking" },
    { icon: <Filter className="w-6 h-6" />, text: "Visual lead pipeline with drag-and-drop stages" },
    { icon: <Star className="w-6 h-6" />, text: "Automated review collection & 5-star funnel" },
    { icon: <Share2 className="w-6 h-6" />, text: "Customer referral tracking with rewards" }
  ];

  const howItWorks = [
    {
      step: "1",
      title: "Lead Comes In",
      description: "Instant estimate delivered automatically—no delays, no manual work."
    },
    {
      step: "2", 
      title: "Automated Follow-Up",
      description: "Your follow-up sequence nurtures the homeowner while you focus on other jobs."
    },
    {
      step: "3",
      title: "Contract & Payment",
      description: "Contract signed digitally, payment collected—all without chasing paperwork."
    },
    {
      step: "4",
      title: "Insurance Claims",
      description: "Insurance claim generated and tracked through to payment."
    }
  ];

  const whyChooseUs = [
    { icon: <Rocket className="w-6 h-6" />, text: "Respond to leads in under 60 seconds" },
    { icon: <Award className="w-6 h-6" />, text: "Win jobs without lowering price" },
    { icon: <Clock className="w-6 h-6" />, text: "Reduce admin work" },
    { icon: <TrendingUp className="w-6 h-6" />, text: "Improve cash flow" },
    { icon: <Users className="w-6 h-6" />, text: "Look more professional than competitors" }
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-lg border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <span className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                HomeFixer360
              </span>
            </div>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#problem" className="text-slate-300 hover:text-white transition-colors">The Problem</a>
              <a href="#solution" className="text-slate-300 hover:text-white transition-colors">Solution</a>
              <a href="#how-it-works" className="text-slate-300 hover:text-white transition-colors">How It Works</a>
              <a href="#pipeline" className="text-slate-300 hover:text-white transition-colors">Pipeline</a>
              <a href="#reviews" className="text-slate-300 hover:text-white transition-colors">Reviews</a>
              <a href="#brand-presence" className="text-slate-300 hover:text-white transition-colors">Brand Presence</a>
              <a href="/guaranteed-leads" className="text-cyan-400 hover:text-cyan-300 transition-colors font-medium">Guaranteed Leads</a>
              <Button 
                onClick={handleBookDemo}
                className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white px-6"
              >
                Book Demo
              </Button>
            </div>

            {/* Mobile menu button */}
            <button 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Nav */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-900 border-t border-slate-800 px-4 py-4 space-y-4">
            <a href="#problem" className="block text-slate-300 hover:text-white">The Problem</a>
            <a href="#solution" className="block text-slate-300 hover:text-white">Solution</a>
            <a href="#how-it-works" className="block text-slate-300 hover:text-white">How It Works</a>
            <a href="#pipeline" className="block text-slate-300 hover:text-white">Pipeline</a>
            <a href="#reviews" className="block text-slate-300 hover:text-white">Reviews</a>
            <a href="#brand-presence" className="block text-slate-300 hover:text-white">Brand Presence</a>
            <a href="/guaranteed-leads" className="block text-cyan-400 hover:text-cyan-300 font-medium">Guaranteed Leads</a>
            <Button onClick={handleBookDemo} className="w-full bg-blue-600 hover:bg-blue-500">
              Book Demo
            </Button>
          </div>
        )}
      </nav>

      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 px-4 overflow-hidden">
        {/* Background gradient effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-cyan-600/20 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
            Close More Roofing Jobs—
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Without Chasing Leads, Estimates, or Insurance Paperwork
            </span>
          </h1>
          
          <p className="text-xl sm:text-2xl text-slate-300 mb-10 max-w-3xl mx-auto leading-relaxed">
            HomeFixer360 helps home service pros respond instantly, automate follow-up, get contracts signed, get paid faster, and simplify insurance claims—all in one system.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button 
              onClick={handleBookDemo}
              size="lg"
              className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white px-10 py-6 text-lg rounded-full shadow-lg shadow-blue-600/25 hover:shadow-blue-500/40 transition-all"
            >
              <Play className="w-5 h-5 mr-2" />
              See How It Works (15-Minute Demo)
            </Button>
          </div>

          {/* Trust indicators */}
          <div className="mt-16 flex flex-wrap justify-center gap-8 text-slate-400 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>No credit card required</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>Setup in under 10 minutes</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>Cancel anytime</span>
            </div>
          </div>

          {/* Guaranteed Leads Callout */}
          <div className="mt-12 max-w-2xl mx-auto">
            <a 
              href="/guaranteed-leads" 
              className="block bg-gradient-to-r from-cyan-900/50 to-blue-900/50 border border-cyan-500/30 rounded-2xl p-6 hover:border-cyan-400/50 transition-all group"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-cyan-500/20 rounded-xl flex items-center justify-center">
                    <Target className="w-6 h-6 text-cyan-400" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">Need More Leads?</h3>
                    <p className="text-slate-300 text-sm">Get guaranteed roofing leads in your area—pay only for results</p>
                  </div>
                </div>
                <ArrowRight className="w-5 h-5 text-cyan-400 group-hover:translate-x-1 transition-transform" />
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* PROBLEM SECTION */}
      <section id="problem" className="py-20 px-4 bg-slate-900/50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-red-500/10 border border-red-500/20 rounded-full text-red-400 text-sm font-medium mb-4">
              THE PROBLEM
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Roofing Jobs Are Lost on Speed—
              <span className="text-red-400">Not Skill</span>
            </h2>
          </div>
          
          <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 sm:p-12">
            <p className="text-xl sm:text-2xl text-slate-300 leading-relaxed text-center">
              Most home service companies lose jobs because <span className="text-white font-semibold">estimates take too long</span>, <span className="text-white font-semibold">follow-up is inconsistent</span>, and <span className="text-white font-semibold">insurance paperwork slows everything down</span>. 
            </p>
            <p className="text-xl sm:text-2xl text-slate-300 leading-relaxed text-center mt-6">
              Homeowners don't wait—<span className="text-red-400 font-semibold">they go with the fastest, most professional response.</span>
            </p>
          </div>
        </div>
      </section>

      {/* SOLUTION SECTION */}
      <section id="solution" className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-400 text-sm font-medium mb-4">
              THE SOLUTION
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              One System That Handles the 
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent"> Entire Sales Process</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {solutionFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 hover:border-blue-500/50 transition-all hover:shadow-lg hover:shadow-blue-500/10"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500/20 to-cyan-500/20 rounded-lg flex items-center justify-center text-blue-400 mb-4">
                  {feature.icon}
                </div>
                <p className="text-lg text-white font-medium">{feature.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS SECTION */}
      <section id="how-it-works" className="py-20 px-4 bg-slate-900/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-400 text-sm font-medium mb-4">
              HOW IT WORKS
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
              Four Simple Steps to 
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent"> More Closed Jobs</span>
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {howItWorks.map((item, index) => (
              <div key={index} className="relative">
                {/* Connector line */}
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-full w-full h-0.5 bg-gradient-to-r from-blue-500/50 to-transparent z-0"></div>
                )}
                
                <div className="relative z-10 text-center">
                  <div className="w-24 h-24 mx-auto bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl flex items-center justify-center text-4xl font-bold mb-6 shadow-lg shadow-blue-600/25">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-slate-400">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEAD PIPELINE SECTION */}
      <section id="pipeline" className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-purple-500/10 border border-purple-500/20 rounded-full text-purple-400 text-sm font-medium mb-4">
              LEAD PIPELINE
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Never Lose Track of a 
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent"> Lead Again</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Visual Kanban-style pipeline that shows exactly where every lead stands—from first contact to closed deal.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl flex items-center justify-center">
                  <Filter className="w-7 h-7 text-purple-400" />
                </div>
                <h3 className="text-2xl font-bold">Visual Pipeline Stages</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>Drag-and-drop leads between stages</span>
                </li>
                <li className="flex items-center gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>New → Contacted → Quoted → Site Visit → Negotiating → Won/Lost</span>
                </li>
                <li className="flex items-center gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>See total pipeline value at a glance</span>
                </li>
                <li className="flex items-center gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>Filter by stage, date, or team member</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8">
              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl flex items-center justify-center">
                  <BarChart3 className="w-7 h-7 text-purple-400" />
                </div>
                <h3 className="text-2xl font-bold">Pipeline Reports</h3>
              </div>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>Export reports to PDF or CSV</span>
                </li>
                <li className="flex items-center gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>Date range filtering (week, month, quarter, year)</span>
                </li>
                <li className="flex items-center gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>Conversion rate tracking</span>
                </li>
                <li className="flex items-center gap-3 text-slate-300">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span>Multi-user team views for admins</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Pipeline Visual Preview */}
          <div className="bg-slate-800/30 border border-slate-700 rounded-2xl p-6 overflow-hidden">
            <div className="flex gap-4 overflow-x-auto pb-4">
              {['New', 'Contacted', 'Quoted', 'Site Visit', 'Negotiating', 'Won'].map((stage, i) => (
                <div key={stage} className="flex-shrink-0 w-48">
                  <div className={`text-center py-2 px-4 rounded-lg mb-3 font-semibold text-sm ${
                    stage === 'Won' ? 'bg-green-500/20 text-green-400' : 
                    stage === 'New' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-slate-700/50 text-slate-300'
                  }`}>
                    {stage}
                  </div>
                  <div className="space-y-2">
                    {[1, 2].map((_, j) => (
                      <div key={j} className="bg-slate-800 border border-slate-700 rounded-lg p-3 text-sm">
                        <div className="h-3 bg-slate-700 rounded w-3/4 mb-2"></div>
                        <div className="h-2 bg-slate-700/50 rounded w-1/2"></div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* REVIEWS & REFERRALS SECTION */}
      <section id="reviews" className="py-20 px-4 bg-slate-900/50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-yellow-500/10 border border-yellow-500/20 rounded-full text-yellow-400 text-sm font-medium mb-4">
              REVIEWS & REFERRALS
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Turn Happy Customers Into 
              <span className="bg-gradient-to-r from-yellow-400 to-orange-400 bg-clip-text text-transparent"> 5-Star Reviews & Referrals</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Automate review collection and track referrals to grow your business through word-of-mouth.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 hover:border-yellow-500/50 transition-all">
              <div className="w-14 h-14 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-xl flex items-center justify-center mb-6">
                <Star className="w-7 h-7 text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold mb-4">5-Star Review Funnel</h3>
              <p className="text-slate-400 mb-4">
                Automatically ask satisfied customers for reviews at the perfect moment. Filter negative feedback before it goes public.
              </p>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  SMS & email review requests
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Direct links to Google, Yelp, Facebook
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Negative feedback private redirect
                </li>
              </ul>
            </div>
            
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 hover:border-yellow-500/50 transition-all">
              <div className="w-14 h-14 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-xl flex items-center justify-center mb-6">
                <Share2 className="w-7 h-7 text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold mb-4">Referral Tracking</h3>
              <p className="text-slate-400 mb-4">
                Track which customers refer new business. Set up rewards and see your referral network grow.
              </p>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Unique referral links per customer
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Track referral conversions
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Customizable reward amounts
                </li>
              </ul>
            </div>
            
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 hover:border-yellow-500/50 transition-all">
              <div className="w-14 h-14 bg-gradient-to-br from-yellow-500/20 to-orange-500/20 rounded-xl flex items-center justify-center mb-6">
                <MessageSquare className="w-7 h-7 text-yellow-400" />
              </div>
              <h3 className="text-xl font-bold mb-4">Review Display Widget</h3>
              <p className="text-slate-400 mb-4">
                Showcase your best reviews on your website and landing pages to build instant trust.
              </p>
              <ul className="space-y-2 text-sm text-slate-300">
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Embeddable review widgets
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Auto-sync from Google
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  Star rating display
                </li>
              </ul>
            </div>
          </div>

          {/* Social Proof Stats */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="flex justify-center gap-1 mb-2">
                {[1,2,3,4,5].map(i => <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />)}
              </div>
              <p className="text-3xl font-bold text-white">4.9</p>
              <p className="text-slate-400 text-sm">Average Rating</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-white">87%</p>
              <p className="text-slate-400 text-sm">Review Response Rate</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-white">3x</p>
              <p className="text-slate-400 text-sm">More Referrals</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-white">24hr</p>
              <p className="text-slate-400 text-sm">Avg Review Time</p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY CONTRACTORS CHOOSE US SECTION */}
      <section className="py-20 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-green-500/10 border border-green-500/20 rounded-full text-green-400 text-sm font-medium mb-4">
              WHY ROOFTRACK360
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold">
              Why Contractors Choose 
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent"> HomeFixer360</span>
            </h2>
          </div>
          
          <div className="space-y-4">
            {whyChooseUs.map((item, index) => (
              <div 
                key={index}
                className="flex items-center gap-4 bg-slate-800/50 border border-slate-700 rounded-xl p-5 hover:border-green-500/50 transition-all"
              >
                <div className="w-12 h-12 bg-gradient-to-br from-green-500/20 to-emerald-500/20 rounded-lg flex items-center justify-center text-green-400 flex-shrink-0">
                  {item.icon}
                </div>
                <p className="text-lg text-white font-medium">{item.text}</p>
                <CheckCircle className="w-6 h-6 text-green-500 ml-auto flex-shrink-0" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OPTIONAL CLOSE-RATE BOOSTER SECTION */}
      <section className="py-20 px-4 bg-slate-900/50">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-slate-800 to-slate-800/50 border border-slate-700 rounded-2xl p-8 sm:p-12">
            <div className="text-center mb-8">
              <span className="inline-block px-4 py-1 bg-amber-500/10 border border-amber-500/20 rounded-full text-amber-400 text-sm font-medium mb-4">
                CLOSE-RATE BOOSTER
              </span>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4">
                Win More Jobs—
                <span className="text-amber-400">Without Discounting</span>
              </h2>
            </div>
            
            <p className="text-lg text-slate-300 text-center max-w-2xl mx-auto">
              Some contractors use homeowner incentives to increase close rates without cutting margins. This is <span className="text-white font-semibold">optional</span> and used strategically—not as a gimmick.
            </p>
          </div>
        </div>
      </section>

      {/* ONLINE BRAND PRESENCE SECTION */}
      <section id="brand-presence" className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-4">
              PREMIUM ADD-ON
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
              Dominate Your Local Market With
              <span className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent"> Online Brand Presence</span>
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              Let us build and manage your complete digital presence—so you can focus on your trade while leads come to you.
            </p>
          </div>
          
          {/* Pricing Banner */}
          <div className="bg-gradient-to-r from-emerald-600/20 to-teal-600/20 border border-emerald-500/30 rounded-2xl p-8 mb-12 text-center">
            <div className="flex flex-wrap justify-center gap-8 items-center">
              <div>
                <p className="text-4xl font-bold text-white">$1,000</p>
                <p className="text-emerald-400">One-Time Setup</p>
              </div>
              <div className="text-3xl text-slate-500">+</div>
              <div>
                <p className="text-4xl font-bold text-white">$400<span className="text-xl">/mo</span></p>
                <p className="text-emerald-400">Ongoing Management</p>
              </div>
            </div>
          </div>

          {/* Features Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 hover:border-emerald-500/50 transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-xl flex items-center justify-center mb-4">
                <Globe className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-lg font-bold mb-2">Professional Website</h3>
              <p className="text-slate-400 text-sm">
                Custom-designed, mobile-optimized website that showcases your work and converts visitors into leads.
              </p>
            </div>
            
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 hover:border-emerald-500/50 transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-xl flex items-center justify-center mb-4">
                <Search className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-lg font-bold mb-2">Local SEO</h3>
              <p className="text-slate-400 text-sm">
                Rank higher on Google for roofing searches in your area. Stop paying for unqualified leads from directories.
              </p>
            </div>
            
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 hover:border-emerald-500/50 transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-xl flex items-center justify-center mb-4">
                <MapPin className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-lg font-bold mb-2">Google Business Profile</h3>
              <p className="text-slate-400 text-sm">
                Fully optimized Google Business presence to dominate local search results and maps.
              </p>
            </div>
            
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 hover:border-emerald-500/50 transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-xl flex items-center justify-center mb-4">
                <Star className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-lg font-bold mb-2">Review Generation</h3>
              <p className="text-slate-400 text-sm">
                Automated system to collect 5-star reviews and filter negative feedback before it goes public.
              </p>
            </div>
            
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 hover:border-emerald-500/50 transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-xl flex items-center justify-center mb-4">
                <Phone className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-lg font-bold mb-2">Missed Call Text-Back</h3>
              <p className="text-slate-400 text-sm">
                Never lose a lead. Automatic text response when you miss a call keeps the conversation going.
              </p>
            </div>
            
            <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 hover:border-emerald-500/50 transition-all">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-500/20 to-teal-500/20 rounded-xl flex items-center justify-center mb-4">
                <Smartphone className="w-6 h-6 text-emerald-400" />
              </div>
              <h3 className="text-lg font-bold mb-2">Mobile-First Design</h3>
              <p className="text-slate-400 text-sm">
                87% of customers browse on mobile. We ensure a perfect experience on every device.
              </p>
            </div>
          </div>

          {/* Results Stats */}
          <div className="bg-slate-800/30 border border-slate-700 rounded-2xl p-8">
            <h3 className="text-xl font-bold text-center mb-8">Results Our Clients See</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div>
                <p className="text-3xl font-bold text-emerald-400">154%</p>
                <p className="text-slate-400 text-sm">Average Growth</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-emerald-400">5,000+</p>
                <p className="text-slate-400 text-sm">Leads Generated</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-emerald-400">7-10</p>
                <p className="text-slate-400 text-sm">Days to Launch</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-emerald-400">112+</p>
                <p className="text-slate-400 text-sm">5-Star Reviews</p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mt-12">
            <Button 
              onClick={handleBrandPresenceBooking}
              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white px-8 py-6 text-lg font-semibold"
            >
              <Calendar className="w-5 h-5 mr-2" />
              Book Brand Presence Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="text-slate-500 text-sm mt-4">Free 20-minute consultation call</p>
          </div>
        </div>
      </section>

      {/* Brand Presence Booking Modal */}
      {showBrandPresenceBooking && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-slate-700">
              <div className="flex items-center gap-3">
                <Calendar className="w-6 h-6 text-emerald-400" />
                <h2 className="text-xl font-bold text-white">Book Your Brand Presence Consultation</h2>
              </div>
              <button 
                onClick={() => setShowBrandPresenceBooking(false)}
                className="p-2 hover:bg-slate-800 rounded-full transition-colors"
              >
                <X className="w-6 h-6 text-slate-400" />
              </button>
            </div>
            <div className="p-4 bg-white" style={{ height: '600px' }}>
              <iframe 
                src={BRAND_PRESENCE_BOOKING_URL}
                style={{ width: '100%', height: '100%', border: 'none', overflow: 'hidden' }}
                scrolling="no"
                title="Book Brand Presence Consultation"
              />
            </div>
          </div>
        </div>
      )}

      {/* FINAL CTA SECTION */}
      <section className="py-24 px-4 relative overflow-hidden">
        {/* Background effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-blue-600/10 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            See If HomeFixer360 Is 
            <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent"> a Fit</span>
          </h2>
          
          <p className="text-xl text-slate-300 mb-10">
            Join hundreds of home service pros who close more jobs with less effort.
          </p>
          
          <Button 
            onClick={handleBookDemo}
            size="lg"
            className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500 text-white px-12 py-6 text-xl rounded-full shadow-lg shadow-blue-600/25 hover:shadow-blue-500/40 transition-all"
          >
            <Calendar className="w-6 h-6 mr-2" />
            Book a 15-Minute Demo
            <ArrowRight className="w-6 h-6 ml-2" />
          </Button>
          
          <p className="mt-6 text-slate-500 text-sm">
            Free demo • No obligation • See results in 15 minutes
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 border-t border-slate-800">
        <div className="max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center">
              <span className="text-xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                HomeFixer360
              </span>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 text-slate-400 text-sm">
              <a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a>
              <a href="/terms" className="hover:text-white transition-colors">Terms of Service</a>
              <a href="mailto:support@homefixer360.com" className="hover:text-white transition-colors">Contact</a>
            </div>
            
            <p className="text-slate-500 text-sm">
              © {new Date().getFullYear()} HomeFixer360. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default ContractorLandingPage;
